import React, { useMemo, useState } from 'react';
import { MessageDescriptor, useIntl } from 'react-intl';
import { TypedMemo } from '../../common/types';
import { Segmented } from '../components/primitives/Segmented';
import { SocialSparkline } from '../promote/SocialSparkline';
import { SerializedSocialPost, SerializedSocialPostDay } from '../promote/types';
import { availableChartMetrics, dailyDifference, SocialPostChartMetric } from './dailyDifference';
import { socialMessages as messages } from './socialMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet per screen; each part of the screen uses its own slice of it */
import styles from './Social.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface SocialPostDailyChartProps {
  post: SerializedSocialPost;
  /** The daily rows of the post, oldest first. They hold running totals. */
  days: SerializedSocialPostDay[];
}

const metricMessages: Record<SocialPostChartMetric, MessageDescriptor> = {
  views: messages.views,
  likes: messages.likes,
  comments: messages.comments,
  shares: messages.shares,
  saves: messages.saves,
  linkClicks: messages.linkClicks,
};

/** The numbers whose switch label is shorter on a phone: "Comm.", "Clicks". */
const shortMessages: Partial<Record<SocialPostChartMetric, MessageDescriptor>> = {
  comments: messages.commentsShort,
  linkClicks: messages.linkClicksShort,
};

/** Parses a `YYYY-MM-DD` UTC day so that the label shows the same day in every time zone. */
function utcDay(day: string): Date {
  return new Date(`${day}T12:00:00.000Z`);
}

/**
 * The daily line of a post. The rows hold running totals, so the line shows the difference
 * between days; the first day has no point. A post with one row has no line yet.
 */
const SocialPostDailyChartComponent: React.FC<SocialPostDailyChartProps> = ({ post, days }) => {
  const intl = useIntl();
  const metrics = useMemo(() => availableChartMetrics(post), [post]);
  const [picked, setPicked] = useState<SocialPostChartMetric>('views');
  // A number that the platform does not give is not in the switch.
  const metric = metrics.includes(picked) ? picked : (metrics[0] ?? 'views');

  const points = useMemo(
    () =>
      dailyDifference(days, metric).filter((point): point is { date: string; value: number } => point.value !== null),
    [days, metric]
  );
  const values = useMemo(() => points.map((point) => point.value), [points]);
  const options = useMemo(
    () =>
      metrics.map((value) => {
        const short = shortMessages[value];
        return {
          value,
          label:
            short === undefined ? (
              intl.formatMessage(metricMessages[value])
            ) : (
              <>
                <span className={styles.desktopOnly}>{intl.formatMessage(metricMessages[value])}</span>
                <span className={styles.phoneOnly}>{intl.formatMessage(short)}</span>
              </>
            ),
        };
      }),
    [intl, metrics]
  );

  const first = points[0];
  const last = points[points.length - 1];
  const axisDay = (day: string) => intl.formatDate(utcDay(day), { month: 'short', day: 'numeric', timeZone: 'UTC' });

  return (
    <div className={styles.chart}>
      <div className={styles.chartHead}>
        <span className={styles.chartTitle}>
          {intl.formatMessage(messages.detailChartTitle, { metric: intl.formatMessage(metricMessages[metric]) })}
        </span>
        {metrics.length > 1 ? (
          <Segmented
            className={styles.chartSwitch}
            options={options}
            value={metric}
            onChange={setPicked}
            ariaLabel={intl.formatMessage(messages.detailMetric)}
          />
        ) : null}
      </div>
      {points.length === 0 || first === undefined || last === undefined ? (
        <div className={styles.chartEmpty}>{intl.formatMessage(messages.detailOneRead)}</div>
      ) : (
        <>
          <SocialSparkline values={values} tall gradientId="social-post-daily" />
          <div className={styles.chartAxis}>
            <span>{axisDay(first.date)}</span>
            <span>{points.length > 1 ? axisDay(last.date) : ''}</span>
          </div>
        </>
      )}
    </div>
  );
};

export const SocialPostDailyChart = TypedMemo(SocialPostDailyChartComponent);
